import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Separator } from "../../components/ui/separator";
import { Loader2, RefreshCw, Trash2, Database, AlertTriangle, TrendingUp, Flame, Clock, Save, Play, Activity, ShieldCheck, ListRestart, CheckCircle2, XCircle, Mail, Calendar } from 'lucide-react';
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { ScrollArea } from "../../components/ui/scroll-area";
import moment from 'moment';

interface CronJob {
  id: string;
  name: string;
  description: string;
  intervalHours: number;
  isActive: boolean;
  lastRunAt: string | null;
  nextRunAt: string | null;
}

interface CronLog {
  id: string;
  jobId: string;
  jobName: string;
  status: 'SUCCESS' | 'FAILED';
  message?: string;
  durationMs: number;
  startedAt: string;
  finishedAt: string;
  createdAt: string;
}

export default function Maintenance() {
  const [loading, setLoading] = useState<string | null>(null);
  const [cronJobs, setCronJobs] = useState<CronJob[]>([]);
  const [cronLogs, setCronLogs] = useState<CronLog[]>([]);
  const [disableRegistration, setDisableRegistration] = useState<boolean>(false);
  const [autoUpdateEnabled, setAutoUpdateEnabled] = useState<boolean>(false);
  const [autoUpdateInterval, setAutoUpdateInterval] = useState<number>(4);
  const [postPresenceInterval, setPostPresenceInterval] = useState<number>(4);
  const [bangerCommentWeight, setBangerCommentWeight] = useState<number>(10);
  const [bangerMinPosts, setBangerMinPosts] = useState<number>(10);
  const [bangerXpReward, setBangerXpReward] = useState<number>(500);
  const [tiktokEnv, setTiktokEnv] = useState<'production' | 'staging'>('production');
  const [tiktokForceSelfOnly, setTiktokForceSelfOnly] = useState<boolean>(false);
  const [tiktokAuditMock1B, setTiktokAuditMock1B] = useState<boolean>(false);
  const [testEmailAddress, setTestEmailAddress] = useState<string>('');
  const [testEmailSubject, setTestEmailSubject] = useState<string>('');
  const [testEmailBody, setTestEmailBody] = useState<string>('');
  const [isTestEmailModalOpen, setIsTestEmailModalOpen] = useState<boolean>(false);
  const [savingCron, setSavingCron] = useState<string | null>(null);
  const [runningCron, setRunningCron] = useState<string | null>(null);
  const [isSavingReg, setIsSavingReg] = useState<boolean>(false);
  const [isRefreshingLogs, setIsRefreshingLogs] = useState<boolean>(false);
  const token = localStorage.getItem('token');

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCronJobs(response.data.cronJobs);
      setDisableRegistration(response.data.disableRegistration);
      setAutoUpdateEnabled(response.data.autoUpdateOnUserList);
      setAutoUpdateInterval(response.data.autoUpdateOnUserListInterval);
      setPostPresenceInterval(response.data.postPresenceCheckInterval || 4);
      setBangerCommentWeight(response.data.bangerCommentWeight);
      setBangerMinPosts(response.data.bangerMinPosts);
      setBangerXpReward(response.data.bangerXpReward);
      setTiktokEnv(response.data.tiktokEnv || 'production');
      setTiktokForceSelfOnly(response.data.tiktokForceSelfOnly || false);
      setTiktokAuditMock1B(response.data.tiktokAuditMock1B || false);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchCronLogs = async () => {
    setIsRefreshingLogs(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/cron-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCronLogs(response.data);
    } catch (error) {
      console.error('Error fetching cron logs:', error);
    } finally {
      setIsRefreshingLogs(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchCronLogs();
  }, []);

  const updateRegistrationStatus = async (disabled: boolean) => {
    setIsSavingReg(true);
    try {
      await axios.put(`${API_BASE_URL}/admin/settings/registration`, { disabled }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDisableRegistration(disabled);
      toast.success(`Registration ${disabled ? 'disabled' : 'enabled'}`);
    } catch (error) {
      toast.error('Failed to update registration status');
    } finally {
      setIsSavingReg(false);
    }
  };

  const updateAutoUpdateSettings = async (updates: { enabled?: boolean, intervalHours?: number, postPresenceCheckInterval?: number }) => {
    try {
      await axios.put(`${API_BASE_URL}/admin/settings/auto-update`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (updates.enabled !== undefined) setAutoUpdateEnabled(updates.enabled);
      if (updates.intervalHours !== undefined) setAutoUpdateInterval(updates.intervalHours);
      if (updates.postPresenceCheckInterval !== undefined) setPostPresenceInterval(updates.postPresenceCheckInterval);
      toast.success('System sync settings updated');
    } catch (error) {
      toast.error('Failed to update system sync settings');
    }
  };

  const updateBangerSettings = async (updates: { commentWeight?: number, minPosts?: number, xpReward?: number }) => {
    try {
      await axios.put(`${API_BASE_URL}/admin/settings/banger`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Banger logic settings updated');
    } catch (error) {
      toast.error('Failed to update banger settings');
    }
  };

  const updateTikTokEnv = async (env: 'production' | 'staging') => {
    try {
      await axios.put(`${API_BASE_URL}/admin/settings/tiktok-env`, { env }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTiktokEnv(env);
      toast.success(`TikTok environment switched to ${env.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to update TikTok environment');
    }
  };

  const updateTikTokForceSelfOnly = async (force: boolean) => {
    try {
      await axios.put(`${API_BASE_URL}/admin/settings/tiktok-force-self-only`, { force }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTiktokForceSelfOnly(force);
      toast.success(`TikTok force SELF_ONLY ${force ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update TikTok force SELF_ONLY');
    }
  };

  const updateTikTokAuditMock = async (enabled: boolean) => {
    try {
      await axios.put(`${API_BASE_URL}/admin/settings/tiktok-audit-mock`, { enabled }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTiktokAuditMock1B(enabled);
      toast.success(`TikTok audit mock ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update TikTok audit mock');
    }
  };

  const updateCronJob = async (id: string, updates: Partial<CronJob>) => {
    setSavingCron(id);
    try {
      await axios.put(`${API_BASE_URL}/admin/settings/cron/${id}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cron job updated');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to update cron job');
    } finally {
      setSavingCron(null);
    }
  };

  const triggerCronJobManual = async (id: string) => {
    setRunningCron(id);
    try {
      await axios.post(`${API_BASE_URL}/admin/settings/cron/${id}/run`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cron job triggered manually');
      // Small delay to let the job finish and log be created before refreshing
      setTimeout(fetchCronLogs, 1500);
    } catch (error) {
      toast.error('Failed to trigger cron job');
    } finally {
      setRunningCron(null);
    }
  };

  const runAction = async (action: string, endpoint: string, data: any = {}) => {
    setLoading(action);
    try {
      const response = await axios.post(`${API_BASE_URL}/admin/${endpoint}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || `${action} completed successfully`);
    } catch (error) {
      console.error(`Maintenance Error (${action}):`, error);
      toast.error(`Failed to run ${action}: ${(error as any).response?.data?.error || (error as any).message}`);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card className="bg-card border-border text-foreground shadow-xl overflow-hidden md:col-span-2">
        <div className="h-1 bg-red-500/50 w-full" />
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <ShieldCheck className="h-5 w-5 text-red-400" />
            System Access Control
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Manage global access and registration settings.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-background rounded-xl border border-border hover:border-red-500/30 transition-colors">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h4 className="font-bold text-foreground">Disable New Registrations</h4>
                {disableRegistration && <Badge variant="outline" className="text-[8px] border-red-500/30 text-red-500 uppercase font-black">Active</Badge>}
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed max-w-2xl">
                When enabled, the registration page will be blocked and new users won't be able to create accounts.
              </p>
            </div>
            <div className="flex items-center gap-4">
              {isSavingReg && <Loader2 className="h-4 w-4 animate-spin text-red-500" />}
              <Switch 
                checked={disableRegistration}
                onCheckedChange={updateRegistrationStatus}
                disabled={isSavingReg}
                className="data-[state=checked]:bg-red-600"
              />
            </div>
          </div>

          <Separator className="my-4 bg-card" />

          <div className="flex items-center justify-between p-4 bg-background rounded-xl border border-border hover:border-primary/30 transition-colors">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h4 className="font-bold text-foreground">TikTok API Environment</h4>
                <Badge variant="outline" className={`text-[8px] uppercase font-black ${tiktokEnv === 'production' ? 'border-green-500/30 text-green-500' : 'border-amber-500/30 text-amber-500'}`}>
                  {tiktokEnv}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed max-w-2xl">
                Switch between Production and Staging TikTok credentials. Staging is used for development and testing.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center bg-background p-1 rounded-lg border border-border">
                <Button 
                  size="sm" 
                  variant={tiktokEnv === 'production' ? 'default' : 'ghost'}
                  onClick={() => updateTikTokEnv('production')}
                  className={`h-7 text-[10px] font-bold px-3 ${tiktokEnv === 'production' ? 'bg-green-600 hover:bg-green-500 text-white' : 'text-muted-foreground'}`}
                >
                  PRODUCTION
                </Button>
                <Button 
                  size="sm" 
                  variant={tiktokEnv === 'staging' ? 'default' : 'ghost'}
                  onClick={() => updateTikTokEnv('staging')}
                  className={`h-7 text-[10px] font-bold px-3 ${tiktokEnv === 'staging' ? 'bg-amber-600 hover:bg-amber-500 text-white' : 'text-muted-foreground'}`}
                >
                  STAGING
                </Button>
              </div>
            </div>
          </div>

          <Separator className="my-4 bg-card" />

          <div className="flex items-center justify-between p-4 bg-background rounded-xl border border-border hover:border-primary/30 transition-colors">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h4 className="font-bold text-foreground">TikTok Force SELF_ONLY</h4>
                <Badge variant="outline" className={`text-[8px] uppercase font-black px-1.5 py-0 ${tiktokForceSelfOnly ? 'text-amber-400 border-amber-400/30' : 'text-muted-foreground border-border'}`}>
                  {tiktokForceSelfOnly ? 'Forced' : 'Normal'}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed max-w-2xl">
                Force all TikTok posts to use "SELF_ONLY" privacy level. Essential for non-audited/sandbox apps to work during review.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Switch 
                checked={tiktokForceSelfOnly}
                onCheckedChange={updateTikTokForceSelfOnly}
                className="data-[state=checked]:bg-amber-600"
              />
            </div>
          </div>

          <Separator className="my-4 bg-card" />

          <div className="flex items-center justify-between p-4 bg-background rounded-xl border border-border hover:border-red-500/30 transition-colors">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h4 className="font-bold text-foreground">TikTok Audit Mock (1.b)</h4>
                <Badge variant="outline" className={`text-[8px] uppercase font-black px-1.5 py-0 ${tiktokAuditMock1B ? 'text-red-400 border-red-400/30' : 'text-muted-foreground border-border'}`}>
                  {tiktokAuditMock1B ? 'Active' : 'Off'}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed max-w-2xl">
                Simulate a TikTok API error (reach_limit) when fetching creator info. Used to demonstrate compliance with TikTok Audit Pt 1.b.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Switch 
                checked={tiktokAuditMock1B}
                onCheckedChange={updateTikTokAuditMock}
                className="data-[state=checked]:bg-red-600"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border text-foreground shadow-xl overflow-hidden md:col-span-2">
        <div className="h-1 bg-primary/50 w-full" />
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <RefreshCw className="h-5 w-5 text-primary" />
            Automatic Stats Synchronization
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Configure how and when post statistics are automatically updated for users.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-background rounded-xl border border-border hover:border-primary/30 transition-colors gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h4 className="font-bold text-foreground">Sync on User List Access</h4>
                {autoUpdateEnabled && <Badge variant="outline" className="text-[8px] border-primary/30 text-primary uppercase font-black">Active</Badge>}
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed max-w-2xl">
                When enabled, accessing the <code className="text-primary">/user</code> endpoint will trigger a background update of post stats if the last update was older than the specified interval.
              </p>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex flex-col items-end gap-1">
                <Label className="text-[9px] uppercase font-black tracking-widest text-muted-foreground">Post Check (Hours)</Label>
                <div className="flex items-center gap-2">
                  <Input 
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={postPresenceInterval}
                    onChange={(e) => setPostPresenceInterval(parseFloat(e.target.value) || 0)}
                    onBlur={() => updateAutoUpdateSettings({ postPresenceCheckInterval: postPresenceInterval })}
                    className="bg-background border-border h-8 w-20 text-xs font-bold text-center"
                  />
                  <span className="text-[10px] text-muted-foreground font-bold">h</span>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Label className="text-[9px] uppercase font-black tracking-widest text-muted-foreground">Refresh Interval (Hours)</Label>
                <div className="flex items-center gap-2">
                  <Input 
                    type="number"
                    min="1"
                    value={autoUpdateInterval}
                    onChange={(e) => setAutoUpdateInterval(parseInt(e.target.value) || 0)}
                    onBlur={() => updateAutoUpdateSettings({ intervalHours: autoUpdateInterval })}
                    className="bg-background border-border h-8 w-20 text-xs font-bold text-center"
                  />
                  <span className="text-[10px] text-muted-foreground font-bold">h</span>
                </div>
              </div>
              <Switch 
                checked={autoUpdateEnabled}
                onCheckedChange={(checked) => updateAutoUpdateSettings({ enabled: checked })}
                className="data-[state=checked]:bg-primary"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border text-foreground shadow-xl overflow-hidden md:col-span-2">
        <div className="h-1 bg-purple-500/50 w-full" />
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Activity className="h-5 w-5 text-purple-400" />
            System Cron Manager
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Monitor and configure all background automated tasks.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {cronJobs.map((job) => (
              <div key={job.id} className="p-4 bg-background rounded-xl border border-border flex flex-col md:flex-row md:items-center justify-between gap-4 group hover:border-border transition-colors">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-3">
                    <Checkbox 
                      id={`active-${job.id}`}
                      checked={job.isActive}
                      onCheckedChange={(checked) => updateCronJob(job.id, { isActive: !!checked })}
                      className="border-border data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                    />
                    <h4 className="font-bold text-foreground">{job.name}</h4>
                    {!job.isActive && <Badge variant="outline" className="text-[8px] border-red-500/30 text-red-500 uppercase font-black">Disabled</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed max-w-2xl">{job.description}</p>
                  
                  <div className="flex flex-wrap gap-x-4 gap-y-2 mt-2">
                    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground font-medium">
                      <Clock className="h-3 w-3" />
                      Last Run: <span className="text-foreground font-bold">{job.lastRunAt ? moment(job.lastRunAt).format('MMM D, HH:mm') : 'Never'}</span>
                      {job.lastRunAt && <span className="text-muted-foreground">({moment(job.lastRunAt).fromNow()})</span>}
                    </div>
                    {job.isActive && job.nextRunAt && (
                      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground font-medium">
                        <Play className="h-3 w-3 text-green-500" />
                        Next Run: <span className="text-green-500/80 font-bold">{moment(job.nextRunAt).fromNow()}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => triggerCronJobManual(job.id)}
                    disabled={runningCron === job.id}
                    className="h-8 px-3 text-[10px] font-bold text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 rounded-lg border border-purple-500/20"
                  >
                    {runningCron === job.id ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : <Play className="h-3 w-3 mr-1.5" />}
                    Run Now
                  </Button>

                  <div className="space-y-1 text-right">
                    <Label className="text-[9px] uppercase font-black tracking-widest text-muted-foreground">Cadence (Hours)</Label>
                    <div className="flex items-center gap-2">
                      <Input 
                        type="number"
                        step="0.1"
                        value={job.intervalHours}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value);
                          const updated = cronJobs.map(j => j.id === job.id ? { ...j, intervalHours: isNaN(val) ? 0 : val } : j);
                          setCronJobs(updated);
                        }}
                        onBlur={() => {
                          const current = cronJobs.find(j => j.id === job.id);
                          if (current) updateCronJob(job.id, { intervalHours: current.intervalHours });
                        }}
                        className="bg-background border-border h-8 w-20 text-xs font-bold text-center"
                      />
                      <span className="text-[10px] text-muted-foreground font-bold">h</span>
                    </div>
                  </div>
                  {savingCron === job.id && <Loader2 className="h-4 w-4 animate-spin text-purple-500" />}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border text-foreground shadow-xl overflow-hidden">
        <div className="h-1 bg-primary/50 w-full" />
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-primary" />
            Social Analytics
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Refresh and synchronize statistics from social media platforms.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button 
            onClick={() => runAction('Update All User Stats', 'update-stats')} 
            disabled={!!loading}
            variant="outline"
            className="w-full justify-start border-border hover:bg-primary/10 hover:text-primary group"
          >
            {loading === 'Update All User Stats' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4 group-hover:rotate-180 transition-transform duration-500" />}
            Refresh All Post Stats
          </Button>
          <p className="text-[10px] text-muted-foreground px-1">
            Triggers a background job to fetch latest likes/comments for all published posts across all users.
          </p>
        </CardContent>
      </Card>

      <Card className="bg-card border-border text-foreground shadow-xl overflow-hidden">
        <div className="h-1 bg-amber-500/50 w-full" />
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-amber-400" />
            System & Trends
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Maintenance of trends data and temporary storage.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Button 
              onClick={() => runAction('Fetch Latest Trends', 'fetch-trends')} 
              disabled={!!loading}
              variant="outline"
              className="w-full justify-start border-border hover:bg-primary/10 hover:text-primary"
            >
              {loading === 'Fetch Latest Trends' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <TrendingUp className="mr-2 h-4 w-4" />}
              Fetch Daily Trends
            </Button>
            <p className="text-[10px] text-muted-foreground px-1 italic">
              Analyzes monitored accounts using AI to discover new trends.
            </p>
          </div>

          <div className="space-y-2">
            <Button 
              onClick={() => {
                if (window.confirm('This will delete ALL trends from the database. Are you sure?')) {
                  runAction('Reset Trends Database', 'clear-trends');
                }
              }} 
              disabled={!!loading}
              variant="outline"
              className="w-full justify-start border-red-900/30 hover:bg-red-500/10 hover:text-red-400 text-red-500/70"
            >
              {loading === 'Reset Trends Database' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Wipe Trends Database
            </Button>
            <p className="text-[10px] text-muted-foreground px-1 italic">
              Removes all trends and evidence. Useful for fixing data corruption or starting fresh.
            </p>
          </div>

          <Separator className="bg-card/50" />

          <div className="space-y-2">
            <Button 
              onClick={() => {
                if (window.confirm('This will remove all pending daily missions for all users so they can be regenerated. Continue?')) {
                  runAction('Reset Daily Missions', 'clear-recommendations');
                }
              }} 
              disabled={!!loading}
              variant="outline"
              className="w-full justify-start border-orange-900/30 hover:bg-orange-500/10 hover:text-orange-400 text-orange-500/70"
            >
              {loading === 'Reset Daily Missions' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Flame className="mr-2 h-4 w-4" />}
              Reset Daily Missions
            </Button>
            <p className="text-[10px] text-muted-foreground px-1 italic">
              Clears today's pending missions for all users, forcing the system to regenerate them on next visit.
            </p>
          </div>

          <Separator className="bg-card/50" />

          <div className="space-y-2">
            <Button 
              onClick={() => runAction('Refresh Steam Events', 'refresh-steam-events')} 
              disabled={!!loading}
              variant="outline"
              className="w-full justify-start border-border hover:bg-primary/10 hover:text-primary"
            >
              {loading === 'Refresh Steam Events' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Calendar className="mr-2 h-4 w-4" />}
              Refresh Steam Events
            </Button>
            <p className="text-[10px] text-muted-foreground px-1 italic">
              Scrapes SteamDB to update the upcoming sales and festivals list.
            </p>
          </div>

          <Separator className="bg-card/50" />

          <Button 
            onClick={() => runAction('Cleanup Orphaned Media', 'cleanup-media')} 
            disabled={!!loading}
            variant="outline"
            className="w-full justify-start border-border hover:bg-amber-500/10 hover:text-amber-400"
          >
            {loading === 'Cleanup Orphaned Media' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <AlertTriangle className="mr-2 h-4 w-4" />}
            Cleanup Temporary Files
          </Button>

          <Separator className="bg-card/50" />

          <Separator className="bg-card/50" />

          <Dialog open={isTestEmailModalOpen} onOpenChange={setIsTestEmailModalOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="outline"
                className="w-full justify-start border-border hover:bg-primary/10 hover:text-primary"
              >
                <Mail className="mr-2 h-4 w-4" />
                Send Custom Test Email
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border text-foreground sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  Custom Test Email
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  Send a manual email to test your SMTP and DNS configuration.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="to_email" className="text-xs uppercase font-bold text-muted-foreground">To Email</Label>
                  <Input 
                    id="to_email"
                    placeholder="example@mail-tester.com"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                    className="bg-background border-border text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject" className="text-xs uppercase font-bold text-muted-foreground">Subject</Label>
                  <Input 
                    id="subject"
                    placeholder="Test Email Subject"
                    value={testEmailSubject}
                    onChange={(e) => setTestEmailSubject(e.target.value)}
                    className="bg-background border-border text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="body" className="text-xs uppercase font-bold text-muted-foreground">Body (HTML allowed)</Label>
                  <Textarea 
                    id="body"
                    placeholder="Hello! This is a test email from my server."
                    value={testEmailBody}
                    onChange={(e) => setTestEmailBody(e.target.value)}
                    className="bg-background border-border min-h-[150px] text-foreground"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button 
                  variant="ghost" 
                  onClick={() => setIsTestEmailModalOpen(false)}
                  disabled={!!loading}
                  className="text-muted-foreground hover:text-foreground"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={async () => {
                    await runAction('Send Test Email', 'send-test-email', { 
                      email: testEmailAddress,
                      subject: testEmailSubject,
                      body: testEmailBody
                    });
                    if (!loading) setIsTestEmailModalOpen(false);
                  }} 
                  disabled={!!loading || !testEmailAddress}
                  className="bg-primary hover:bg-primary/90 text-white"
                >
                  {loading === 'Send Test Email' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
                  Send Email Now
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <p className="text-[10px] text-muted-foreground px-1 italic">
            Opens a form to send a custom email and verify your deliverability (SPF/DKIM/DMARC).
          </p>
        </CardContent>

      </Card>

      <Card className="bg-card border-border text-foreground shadow-xl overflow-hidden md:col-span-2">
        <div className="h-1 bg-amber-500/50 w-full" />
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Flame className="h-5 w-5 text-amber-400" />
            Banger Analysis Logic
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Configure the algorithm that detects high-performing posts and rewards users.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-4 bg-background rounded-xl border border-border">
            <div className="space-y-2">
              <Label className="text-[9px] uppercase font-black tracking-widest text-muted-foreground">Comment Weight (vs Likes)</Label>
              <div className="flex items-center gap-2">
                <Input 
                  type="number"
                  min="1"
                  value={bangerCommentWeight}
                  onChange={(e) => setBangerCommentWeight(parseInt(e.target.value) || 0)}
                  onBlur={() => updateBangerSettings({ commentWeight: bangerCommentWeight })}
                  className="bg-background border-border h-9 w-full text-xs font-bold"
                />
                <span className="text-[10px] text-muted-foreground font-bold whitespace-nowrap">1 comment = X likes</span>
              </div>
              <p className="text-[10px] text-muted-foreground italic">Multiplier for comments when calculating engagement score.</p>
            </div>

            <div className="space-y-2">
              <Label className="text-[9px] uppercase font-black tracking-widest text-muted-foreground">Baseline Posts Requirement</Label>
              <div className="flex items-center gap-2">
                <Input 
                  type="number"
                  min="1"
                  value={bangerMinPosts}
                  onChange={(e) => setBangerMinPosts(parseInt(e.target.value) || 0)}
                  onBlur={() => updateBangerSettings({ minPosts: bangerMinPosts })}
                  className="bg-background border-border h-9 w-full text-xs font-bold"
                />
                <span className="text-[10px] text-muted-foreground font-bold whitespace-nowrap">posts</span>
              </div>
              <p className="text-[10px] text-muted-foreground italic">Minimum number of posts an account must have before banger detection kicks in.</p>
            </div>

            <div className="space-y-2">
              <Label className="text-[9px] uppercase font-black tracking-widest text-muted-foreground">XP Reward</Label>
              <div className="flex items-center gap-2">
                <Input 
                  type="number"
                  min="0"
                  value={bangerXpReward}
                  onChange={(e) => setBangerXpReward(parseInt(e.target.value) || 0)}
                  onBlur={() => updateBangerSettings({ xpReward: bangerXpReward })}
                  className="bg-background border-border h-9 w-full text-xs font-bold text-amber-400"
                />
                <span className="text-[10px] text-muted-foreground font-bold whitespace-nowrap">XP</span>
              </div>
              <p className="text-[10px] text-muted-foreground italic">Experience points awarded to the user when a banger is detected.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border text-foreground shadow-xl overflow-hidden md:col-span-2">
        <div className="h-1 bg-slate-500/50 w-full" />
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-lg">
              <ListRestart className="h-5 w-5 text-muted-foreground" />
              Recent Executions
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              History of the latest background tasks.
            </CardDescription>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={fetchCronLogs} 
            disabled={isRefreshingLogs}
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshingLogs ? 'animate-spin' : ''}`} />
          </Button>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px] w-full rounded-xl border border-border bg-background/50">
            <Table>
              <TableHeader className="bg-card/50 sticky top-0 z-10">
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead className="text-muted-foreground font-bold uppercase tracking-wider text-[10px]">Job Name</TableHead>
                  <TableHead className="text-muted-foreground font-bold uppercase tracking-wider text-[10px]">Status</TableHead>
                  <TableHead className="text-muted-foreground font-bold uppercase tracking-wider text-[10px]">Execution Time</TableHead>
                  <TableHead className="text-muted-foreground font-bold uppercase tracking-wider text-[10px]">Duration</TableHead>
                  <TableHead className="text-muted-foreground font-bold uppercase tracking-wider text-[10px]">Message</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cronLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-10 text-muted-foreground text-sm">
                      No logs found. Run a task to see history.
                    </TableCell>
                  </TableRow>
                ) : (
                  cronLogs.map((log) => (
                    <TableRow key={log.id} className="border-border hover:bg-card/30 transition-colors group">
                      <TableCell className="font-bold text-foreground text-xs">{log.jobName}</TableCell>
                      <TableCell>
                        {log.status === 'SUCCESS' ? (
                          <Badge className="bg-green-500/10 text-green-400 border-green-500/20 gap-1 text-[10px] font-black uppercase">
                            <CheckCircle2 className="h-2.5 w-2.5" />
                            Success
                          </Badge>
                        ) : (
                          <Badge className="bg-red-500/10 text-red-400 border-red-500/20 gap-1 text-[10px] font-black uppercase">
                            <XCircle className="h-2.5 w-2.5" />
                            Failed
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-[10px] text-muted-foreground font-medium">
                        <div className="flex flex-col">
                          <span>{moment(log.startedAt).format('MMM D, HH:mm:ss')}</span>
                          <span className="text-[9px] text-muted-foreground italic">({moment(log.startedAt).fromNow()})</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-[10px] text-muted-foreground font-mono">
                        {log.durationMs ? `${(log.durationMs / 1000).toFixed(2)}s` : '-'}
                      </TableCell>
                      <TableCell className="text-[10px] text-muted-foreground max-w-[200px] truncate group-hover:whitespace-normal group-hover:max-w-none transition-all">
                        {log.message || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}

